<template>
    <div>
        <v-card class="cash-sales" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('cash_sales.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            class="mr-4"
                            color="white"
                            icon
                            @click="showSearchForm = !showSearchForm"
                        >
                            <v-icon>fal fa-filter</v-icon>
                        </v-btn>
                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="openForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <cash-sales-search
                    v-if="showSearchForm"
                    class="mt-3"
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:foot="{ items }">
                        <tfoot>
                            <tr>
                                <td></td>
                                <td>{{ $t('general.total').toUpperCase() }}:</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </template>
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ item.date | date }}</td>
                            <td>{{ item.paid_at_date | date }}</td>
                            <td>{{ item.getAmount().toFormat('0.00') }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            key="edit"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="delete"
                                            :disabled="!!item.recorded_at"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon
                                                    :disabled="!!item.recorded_at"
                                                    :class="{'error--text': !item.recorded_at}"
                                                >
                                                    fal fa-times
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title :class="{'error--text': !item.recorded_at}">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Cash Sale Edit Dialog -->
        <cash-sale-form
            :show.sync="showForm"
            :item="selectedItem"
            @save="onItemSaved"
        />
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import CashSale from '@/models/CashSale';
import CashSaleForm from '@/views/shared/admin/cashSales/CashSaleForm.vue';
import CashSalesSearch from '@/views/shared/admin/cashSales/CashSalesSearch';

export default {
    components: {
        CashSalesSearch,
        CashSaleForm
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            showSearchForm: false,
            showForm: false,
            selectedItem: null,

            filter: {
                search: null,
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
            },

            headers: [
                { text: this.$t('cash_sales.date'), value: 'date' },
                { text: this.$t('cash_sales.paid_at_date'), value: 'paid_at_date' },
                { text: this.$t('cash_sales.amount'), value: 'amount' },
                { text: '', sortable: false }
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.showSearchForm = false;
            this.getItems();
        },
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            const params = { page, itemsPerPage, sortField, sortOrder }

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        params[key] = this.filter[key]
                    }
                })
            }

            this.loading = true;

            ApiService.get('/admin/cash-sales', { params }).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => new CashSale(item));
                this.itemsTotal = response.data.total;
            });
        },

        openForm(item = null) {
            this.selectedItem = item;
            this.showForm = true;
        },

        onItemSaved(item) {
            this.showForm = false;

            if(!this.selectedItem) {
                this.items.unshift(item);
                this.itemsTotal++;
                return;
            }

            const updatedItemIndex = this.items.findIndex(i => i.id === item.id)
            if(updatedItemIndex !== -1) {
                this.$set(this.items, updatedItemIndex, item);
            }

            this.selectedItem = null;
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('cash_sales.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/cash-sales/${item.id}`, {}).then(response => {
                        this.items = this.items.filter(i => i.id !== item.id);
                        this.$snackbar(this.$t('cash_sales.deleted'), 'success');
                        this.itemsTotal--;
                    });
                }
            );
        },
    }
};
</script>

<style lang="scss" scoped>
.cash-sales {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
